/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  Box, Wand2, Upload, Loader2, Download, Trash2, Image as ImageIcon,
  Sparkles, X, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { render3dAPI, moodBoardAPI } from '../lib/api';

const MODES = [
  {
    key: 'compose',
    label: 'Compose (PIL)',
    hint: 'Deterministic 2×2 grid from your existing photos. Free.',
  },
  {
    key: 'ai',
    label: 'AI synthesis (gpt-image-2)',
    hint: 'One source photo → 4 fresh angles generated by gpt-image-2.',
  },
];

export default function RenderGenerator() {
  const [mode, setMode] = useState('compose');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [refs, setRefs] = useState([]);
  const [etsyRefs, setEtsyRefs] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [etsySources, setEtsySources] = useState([]);
  const [etsyPickerOpen, setEtsyPickerOpen] = useState(false);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [expandedEtsy, setExpandedEtsy] = useState(null);
  const fileInputRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const res = await render3dAPI.history();
      setHistory(res.data.items || []);
    } catch (err) {
      console.error('history load failed', err);
    }
  }, []);

  // Poll while any AI generation is still pending (background task pattern).
  useEffect(() => {
    const hasPending = history.some((h) => h.status === 'pending');
    if (!hasPending) return;
    const id = setInterval(() => { loadHistory(); }, 4000);
    return () => clearInterval(id);
  }, [history, loadHistory]);

  const loadEtsySources = useCallback(async () => {
    try {
      const res = await moodBoardAPI.etsySources();
      setEtsySources(res.data.items || []);
    } catch (err) {
      console.error('etsy sources load failed', err);
    }
  }, []);

  useEffect(() => { loadHistory(); loadEtsySources(); }, [loadHistory, loadEtsySources]);

  const handleUpload = async (e) => {
    const target = e.target;
    const files = Array.from(target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const res = await render3dAPI.uploadReference(f);
        uploaded.push(res.data);
      }
      setRefs((prev) => [...prev, ...uploaded]);
      toast.success(`${files.length} reference${files.length > 1 ? 's' : ''} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeRef = (refId) => setRefs((p) => p.filter((r) => r.ref_id !== refId));

  const toggleEtsyMedia = (productId, productName, photo) => {
    setEtsyRefs((prev) => {
      const exists = prev.find((r) => r.r2_key === photo.r2_key);
      if (exists) return prev.filter((r) => r.r2_key !== photo.r2_key);
      return [...prev, {
        product_id: productId,
        media_id: photo.id,
        r2_key: photo.r2_key,
        url: photo.url,
        product_name: productName,
      }];
    });
  };

  const removeEtsyRef = (r2_key) => setEtsyRefs((p) => p.filter((r) => r.r2_key !== r2_key));

  // "Use whole product" — auto-fill title + description + the product's
  // photos. Mirrors the MoodBoardGenerator flow but caps at 4 photos because
  // the 3D-render AI mode (gpt-image-2 image-edit) only uses up to 4 refs.
  const handleUseWholeProduct = async (p) => {
    try {
      const { etsyAPI } = await import('../lib/api');
      const res = await etsyAPI.get(p.product_id);
      const prod = res.data || {};
      setTitle(prod.tlj_title || prod.ebay_best_title || prod.best_title || prod.name || p.name);
      const desc = (prod.tlj_extras?.story_description
        || prod.description
        || prod.ebay_description
        || prod.ebay_extracted_facts
        || '').toString().slice(0, 2000);
      setDescription(desc);
      const photos = (prod.media || []).filter((m) => m.kind === 'photo' && m.r2_key);
      setEtsyRefs(photos.slice(0, 4).map((m) => ({
        product_id: prod.id,
        media_id: m.id,
        r2_key: m.r2_key,
        url: m.view_url,
        product_name: prod.name || p.name,
      })));
      setRefs([]);
      setProductPickerOpen(false);
      toast.success(`Loaded ${Math.min(photos.length, 4)} of ${photos.length} photo${photos.length === 1 ? '' : 's'} from "${prod.name || p.name}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load product');
    }
  };

  const totalRefs = refs.length + etsyRefs.length;

  const handleGenerate = async () => {
    if (mode === 'compose' && totalRefs < 2) {
      toast.error('Compose mode needs at least 2 reference photos.');
      return;
    }
    if (mode === 'ai' && totalRefs < 1) {
      toast.error('AI mode needs at least 1 reference photo.');
      return;
    }
    setGenerating(true);
    try {
      const res = await render3dAPI.generate({
        mode,
        title,
        description,
        source_r2_keys: [
          ...refs.map((r) => r.r2_key),
          ...etsyRefs.map((r) => r.r2_key),
        ],
      });
      setHistory((prev) => [res.data, ...prev]);
      if (res.data.status === 'pending') {
        toast.info('AI render started — appears below when ready (~90-180s)');
      } else {
        toast.success('3D render ready');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this render?')) return;
    try {
      await render3dAPI.delete(id);
      setHistory((p) => p.filter((h) => h.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8" data-testid="render-3d-page">
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-zinc-100 text-zinc-700 flex items-center justify-center">
            <Box size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">3D Render Generator</h1>
            <p className="text-sm text-zinc-500">Build clean 4-angle product photography collages from your ring photos — compose them in-place or synthesise fresh angles with gpt-image-2.</p>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: inputs */}
        <div className="space-y-5">
          <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Title (optional)</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Emerald Cut Three Stone — 3D Renders"
                data-testid="render-title-input"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Description / spec brief (optional)</label>
              <Textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="2.5ct emerald cut center · 14K white gold · three stone with trapezoid sides · pavé band"
                data-testid="render-desc-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Mode</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {MODES.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setMode(m.key)}
                    className={`text-left rounded-md border p-3 transition-colors ${
                      mode === m.key ? 'border-rose-500 bg-rose-50/40' : 'border-zinc-200 hover:border-zinc-400'
                    }`}
                    data-testid={`render-mode-${m.key}`}
                  >
                    <div className="text-xs font-semibold text-zinc-900">{m.label}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">{m.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-zinc-900">Reference photos</h3>
                <p className="text-xs text-zinc-500 mt-0.5">{totalRefs} selected</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleUpload}
                  className="hidden"
                  data-testid="render-file-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="render-upload-btn"
                >
                  {uploading ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Upload size={12} className="mr-1.5" />}
                  Upload
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setProductPickerOpen((s) => !s)}
                  data-testid="render-use-product-btn"
                >
                  <ImageIcon size={12} className="mr-1.5" /> Use whole product
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEtsyPickerOpen((s) => !s)}
                  data-testid="render-pick-etsy-btn"
                >
                  <ImageIcon size={12} className="mr-1.5" /> Pick photos
                </Button>
              </div>
            </div>

            {refs.length === 0 && etsyRefs.length === 0 ? (
              <div className="border-2 border-dashed border-zinc-200 rounded-md p-8 text-center text-xs text-zinc-400">
                No references yet. Upload ring photos or pick from existing Etsy listings.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {refs.map((r) => (
                  <div key={r.ref_id} className="relative aspect-square rounded-md overflow-hidden border border-zinc-200">
                    <img src={r.url} alt={r.original_name} className="w-full h-full object-cover" />
                    <button
                      onClick={() => removeRef(r.ref_id)}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white text-[10px] flex items-center justify-center hover:bg-black"
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
                {etsyRefs.map((r) => (
                  <div key={r.r2_key} className="relative aspect-square rounded-md overflow-hidden border-2 border-rose-200">
                    <img src={r.url} alt={r.product_name} className="w-full h-full object-cover" />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate">{r.product_name}</div>
                    <button
                      onClick={() => removeEtsyRef(r.r2_key)}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white text-[10px] flex items-center justify-center hover:bg-black"
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {productPickerOpen && (
              <div className="border border-emerald-200 rounded-md bg-emerald-50/40 p-3 space-y-2 max-h-96 overflow-y-auto" data-testid="render-product-picker">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-800 px-1">
                  Tap a product to auto-fill title + description + every photo
                </div>
                {etsySources.length === 0 ? (
                  <p className="text-xs text-zinc-400 text-center py-4">No Etsy products with photos yet.</p>
                ) : (
                  etsySources.map((p) => (
                    <button
                      key={p.product_id}
                      onClick={() => handleUseWholeProduct(p)}
                      className="w-full bg-white rounded border border-zinc-200 hover:border-emerald-400 text-left p-2.5 flex items-center gap-2"
                      data-testid={`render-use-product-${p.product_id}`}
                    >
                      {p.photos[0] && (
                        <img src={p.photos[0].url} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-zinc-900 truncate">{p.name}</div>
                        <div className="text-[10px] text-zinc-500">{p.photos.length} photos available</div>
                      </div>
                      <ChevronDown size={12} className="text-zinc-400 -rotate-90" />
                    </button>
                  ))
                )}
              </div>
            )}

            {etsyPickerOpen && (
              <div className="border border-zinc-200 rounded-md bg-zinc-50 p-3 space-y-2 max-h-96 overflow-y-auto" data-testid="render-etsy-picker">
                {etsySources.length === 0 ? (
                  <p className="text-xs text-zinc-400 text-center py-4">No Etsy products with photos yet.</p>
                ) : (
                  etsySources.map((p) => (
                    <div key={p.product_id} className="bg-white rounded border border-zinc-200">
                      <button
                        onClick={() => setExpandedEtsy(expandedEtsy === p.product_id ? null : p.product_id)}
                        className="w-full flex items-center justify-between px-3 py-2 text-xs"
                      >
                        <span className="truncate font-medium text-zinc-800">{p.name}</span>
                        <span className="flex items-center gap-1 text-zinc-500">
                          {p.photos.length} photos
                          {expandedEtsy === p.product_id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </span>
                      </button>
                      {expandedEtsy === p.product_id && (
                        <div className="grid grid-cols-4 gap-1.5 p-2 border-t border-zinc-100">
                          {p.photos.map((photo) => {
                            const selected = etsyRefs.some((r) => r.r2_key === photo.r2_key);
                            return (
                              <button
                                key={photo.id}
                                onClick={() => toggleEtsyMedia(p.product_id, p.name, photo)}
                                className={`relative aspect-square rounded overflow-hidden border-2 transition-colors ${
                                  selected ? 'border-rose-500 ring-2 ring-rose-200' : 'border-zinc-200 hover:border-zinc-400'
                                }`}
                              >
                                <img src={photo.url} alt="" className="w-full h-full object-cover" />
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <Button
            onClick={handleGenerate}
            disabled={generating}
            size="lg"
            className="w-full bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid="render-generate-btn"
          >
            {generating ? (
              <><Loader2 size={14} className="mr-2 animate-spin" /> Generating ({mode === 'ai' ? 'AI takes ~60s' : 'instant'})…</>
            ) : (
              <><Sparkles size={14} className="mr-2" /> Generate 3D render</>
            )}
          </Button>
        </div>

        {/* Right: history */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-zinc-900">Your renders</h3>
          {history.length === 0 ? (
            <div className="border-2 border-dashed border-zinc-200 rounded-md p-12 text-center text-xs text-zinc-400">
              No renders yet — generate your first one →
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((h) => (
                <div key={h.id} className={`bg-white border rounded-xl p-3 flex gap-3 ${
                  h.status === 'failed' ? 'border-red-200' : 'border-zinc-200'
                }`} data-testid={`render-history-${h.id}`}>
                  {h.status === 'pending' ? (
                    <div className="flex-shrink-0 w-32 h-32 rounded-md border border-amber-200 bg-amber-50 flex flex-col items-center justify-center text-amber-700">
                      <Loader2 size={20} className="animate-spin" />
                      <span className="text-[9px] mt-1.5">Working…</span>
                    </div>
                  ) : h.status === 'failed' ? (
                    <div className="flex-shrink-0 w-32 h-32 rounded-md border border-red-200 bg-red-50 flex flex-col items-center justify-center text-red-600 px-2">
                      <Trash2 size={18} />
                      <span className="text-[9px] mt-1.5 font-semibold">Failed</span>
                    </div>
                  ) : (
                    <a href={h.view_url} target="_blank" rel="noreferrer" className="flex-shrink-0 w-32 h-32 rounded-md overflow-hidden border border-zinc-200">
                      <img src={h.view_url} alt={h.title} className="w-full h-full object-cover" />
                    </a>
                  )}
                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-zinc-900 truncate">{h.title}</p>
                        <p className="text-[11px] text-zinc-500">{new Date(h.created_at).toLocaleString()}</p>
                      </div>
                      <Badge className={`border text-[10px] uppercase tracking-wider font-normal ${
                        h.status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : h.status === 'failed' ? 'bg-red-50 text-red-700 border-red-200'
                        : 'bg-zinc-100 text-zinc-700 border-zinc-200'
                      }`}>
                        {h.status === 'pending' ? 'Generating…' : h.status === 'failed' ? 'Failed' : h.mode}
                      </Badge>
                    </div>
                    {h.status === 'failed' ? (
                      <div className="mt-1.5 bg-red-50 border border-red-200 rounded p-2 text-[11px] text-red-800 whitespace-pre-wrap break-words" data-testid={`render-error-${h.id}`}>
                        {h.error || 'Generation failed for an unknown reason.'}
                      </div>
                    ) : (
                      <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{h.description || '—'}</p>
                    )}
                    <div className="mt-auto pt-2 flex items-center gap-2">
                      {h.status === 'ready' && (
                        <a
                          href={h.download_url}
                          download={`3d-render-${h.id}.png`}
                          className="inline-flex items-center gap-1 text-xs text-rose-700 hover:text-rose-900"
                        >
                          <Download size={11} /> Download
                        </a>
                      )}
                      <button
                        onClick={() => handleDelete(h.id)}
                        className="ml-auto inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-red-600"
                        data-testid={`render-delete-${h.id}`}
                      >
                        <Trash2 size={11} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
